import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const docAgents = [
  { label: 'Doc Parsing Agent', path: '/agents/doc-parsing', desc: 'The core engine for extraction, classification, and validation of enterprise documents.' },
  { label: 'Summarizer Agent', path: '/agents/summarizer', desc: 'Condense long-form reports, contracts, and filings into actionable executive summaries.' },
  { label: 'CPA Agent', path: '/agents/cpa', desc: 'Process invoices, receipts, and financial statements for automated reconciliation.' },
  { label: 'Governance Audit', path: '/agents/governance-audit', desc: 'Audit documents against corporate policy and regulatory standards automatically.' },
];

const faqs = [
  {
    q: 'What types of documents can Eagentix process?',
    a: 'Eagentix agents can process structured, semi-structured, and unstructured documents including invoices, contracts, medical reports, bills of lading, and regulatory filings.',
  },
  {
    q: 'How accurate is the AI document extraction?',
    a: 'Eagentix uses high-fidelity frontier models combined with a deterministic harness to ensure professional-grade accuracy, with human-in-the-loop escalation for low-confidence results.',
  },
  {
    q: 'Can document data be integrated directly into our ERP?',
    a: 'Yes. Eagentix agents feature native connectors to 100+ enterprise systems, allowing extracted data to flow directly into your existing workflows and databases.',
  },
];

export default function DocumentAI() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI Document Processing: Enterprise Agents | Eagentix"
        description="Automate document workflows with enterprise AI agents. From extraction to contract review, Eagentix provides high-accuracy document AI for any sector."
        pathname="/use-cases/document-ai-automation"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Use Case · Document AI
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Enterprise AI Document Processing
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Turn unstructured documents into structured data. Eagentix provides purpose-built AI agents that extract, classify, and validate document data at scale with professional precision.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Document Solutions */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            AI-First Document Workflows
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Streamline your operations by automating the most document-heavy business functions.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {docAgents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '8px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            High-Fidelity Extraction & Analysis
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix agents go beyond simple OCR, understanding the semantic meaning and context of your documents.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              {
                title: 'Complex Extraction',
                desc: 'Extract nested data from tables, handwritten notes, and complex layouts with model-agnostic intelligence.',
              },
              {
                title: 'Automated Redlining',
                desc: 'Identify policy deviations and missing clauses in legal contracts and corporate agreements instantly.',
              },
              {
                title: 'Cross-Doc Reconciliation',
                desc: 'Validate data across multiple documents, such as matching invoices to purchase orders and shipping manifests.',
              },
            ].map((item, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                <p style={{ color: '#475569', lineHeight: 1.75, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Document AI FAQ
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Eliminate Manual Data Entry
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            See how enterprise AI agents can automate your most document-heavy workflows. Book a session with our experts today.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
